import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout, AdminFilterTrigger, AdminFilterSidebar, AdminSalesChart } from '../components'
import { ImageWithFallback, Input, Select } from '../../../shared/components'
import { buildImageUrl } from '../../../config'
import statsService from '../../../services/statsService'
import {
  AdminReportRange,
  AdminReportsData,
  AdminReportType,
  GetAdminReportsParams
} from '../../../types/api'

const CSS_HANDLES = [
  'adminReports',
  'reportsHeader',
  'reportsTitle',
  'reportsSubtitle',
  'filtersSection',
  'filterGroup',
  'filterLabel',
  'filterSelect',
  'filterInput',
  'filterButton',
  'reportsGrid',
  'reportCard',
  'reportTitle',
  'reportValue',
  'reportChange',
  'reportPositive',
  'reportNegative',
  'chartSection',
  'chartCard',
  'chartTitle',
  'chartContent',
  'chartPlaceholder',
  'tableSection',
  'tableCard',
  'tableTitle',
  'tableContent',
  'tableHeader',
  'tableRow',
  'tableCell',
  'exportSection',
  'exportButton',
  'dateRange',
  'dateInput',
  'summaryCards',
  'summaryCard',
  'summaryIcon',
  'summaryValue',
  'summaryLabel'
] as const

const RANGE_LABELS: Record<AdminReportRange, string> = {
  '7': 'Últimos 7 dias',
  '30': 'Últimos 30 dias',
  '90': 'Últimos 90 dias',
  '365': 'Último ano',
  custom: 'Período personalizado'
}

const TYPE_LABELS: Record<AdminReportType, string> = {
  sales: 'Vendas',
  products: 'Produtos',
  customers: 'Clientes',
  inventory: 'Estoque'
}

const AdminReports: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  type ReportFilters = {
    range: AdminReportRange
    type: AdminReportType
    startDate: string
    endDate: string
  }

  const defaultFilters: ReportFilters = {
    range: '30',
    type: 'sales',
    startDate: '',
    endDate: ''
  }

  const [filters, setFilters] = useState<ReportFilters>(() => ({
    ...defaultFilters
  }))
  const [draftFilters, setDraftFilters] = useState<ReportFilters>(() => ({
    ...defaultFilters
  }))
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)
  const [reportsData, setReportsData] = useState<AdminReportsData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Refs para evitar chamadas duplicadas
  const previousFiltersRef = useRef<string | null>(null)
  const isLoadingRef = useRef(false)

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const formatPercentage = (value: number) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
  }

  const fetchReports = useCallback(
    async (appliedFilters: ReportFilters, options?: { silent?: boolean }) => {
      const { range, type, startDate, endDate } = appliedFilters

      if (range === 'custom' && (!startDate || !endDate)) {
        if (!options?.silent) {
          setError('Selecione uma data inicial e final para o período personalizado.')
        }
        return
      }

      // Serializar filtros para comparação
      const filtersKey = JSON.stringify({
        range,
        type,
        startDate,
        endDate
      })

      // Verificar se já está carregando para evitar chamadas simultâneas
      if (isLoadingRef.current) {
        return
      }

      // Verificar se realmente mudou
      const filtersChanged = previousFiltersRef.current !== filtersKey

      // Se nada mudou e já foi carregado, não fazer nova chamada
      if (!filtersChanged && isLoadingRef.current) {
        return
      }
      if (!filtersChanged && !isLoadingRef.current && previousFiltersRef.current === filtersKey) {
        return
      }

      isLoadingRef.current = true
      previousFiltersRef.current = filtersKey

      setIsLoading(true)
      setError(null)

      try {
        const payload: GetAdminReportsParams = {
          range,
          type
        }

        if (range === 'custom') {
          payload.startDate = startDate
          payload.endDate = endDate
        }

        const data = await statsService.getReports(payload)
        setReportsData(data)
      } catch (err) {
        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: string }).message)
            : 'Não foi possível carregar os relatórios. Tente novamente.'
        setError(message)
        // Resetar refs em caso de erro para permitir nova tentativa
        previousFiltersRef.current = null
      } finally {
        setIsLoading(false)
        isLoadingRef.current = false
      }
    },
    []
  )

  useEffect(() => {
    fetchReports(filters, { silent: true })
  }, [fetchReports, filters])

  const hasActiveFilters =
    filters.range !== defaultFilters.range ||
    filters.type !== defaultFilters.type ||
    (filters.range === 'custom' && (!!filters.startDate || !!filters.endDate))

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters })
    setIsFilterSidebarOpen(true)
  }

  const handleCloseFilters = () => {
    setIsFilterSidebarOpen(false)
  }

  const handleDraftFilterChange = (partial: Partial<ReportFilters>) => {
    setDraftFilters((prev) => ({
      ...prev,
      ...partial
    }))
  }

  const handleApplyFilters = () => {
    setFilters({ ...draftFilters })
    setIsFilterSidebarOpen(false)
  }

  const handleClearFilters = () => {
    setDraftFilters({ ...defaultFilters })
    setFilters({ ...defaultFilters })
  }

  const isApplyDisabled =
    draftFilters.range === filters.range &&
    draftFilters.type === filters.type &&
    draftFilters.startDate === filters.startDate &&
    draftFilters.endDate === filters.endDate

  const isCustomRangeInvalid =
    draftFilters.range === 'custom' &&
    (!draftFilters.startDate || !draftFilters.endDate)

  const isApplyButtonDisabled = isApplyDisabled || isCustomRangeInvalid

  const appliedRangeDescription =
    filters.range === 'custom'
      ? `Período personalizado: ${
          filters.startDate ? new Date(filters.startDate).toLocaleDateString('pt-BR') : 'início não definido'
        } – ${
          filters.endDate ? new Date(filters.endDate).toLocaleDateString('pt-BR') : 'fim não definido'
        }`
      : `Período: ${RANGE_LABELS[filters.range]}`

  const appliedTypeDescription = `Relatório: ${TYPE_LABELS[filters.type]}`

  const summary = reportsData?.summary
  const topProducts = reportsData?.topProducts ?? []
  const topCustomers = reportsData?.topCustomers ?? []
  const salesTrend = reportsData?.salesTrend ?? []

  return (
    <AdminLayout>
      <div className={`${handles.adminReports}`}>
        {/* Header */}
        <div className={`${handles.reportsHeader} mb-8`}>
          <h1 className={`${handles.reportsTitle} mb-2 text-3xl font-bold text-gray-900`}>
            Relatórios de Vendas
          </h1>
          <p className={`${handles.reportsSubtitle} text-lg text-gray-600`}>
            Análise detalhada do desempenho da loja
          </p>
        </div>

        {/* Barra de ações */}
        <div className={`${handles.filtersSection} mb-8 rounded-lg bg-white p-6 shadow-sm`}>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm text-gray-600">
                {appliedRangeDescription}
              </p>
              <p className="text-sm text-gray-600">{appliedTypeDescription}</p>
              {hasActiveFilters && filters.range === 'custom' && (
                <p className="mt-1 text-xs text-blue-600">
                  Período selecionado: {filters.startDate ? new Date(filters.startDate).toLocaleDateString('pt-BR') : '—'} –{' '}
                  {filters.endDate ? new Date(filters.endDate).toLocaleDateString('pt-BR') : '—'}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fetchReports(filters)}
                disabled={isLoading}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoading ? 'Atualizando...' : 'Atualizar dados'}
              </button>
              <AdminFilterTrigger
                onClick={handleOpenFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </div>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-6 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700"
          >
            <div className="flex items-center justify-between gap-4">
              <span>{error}</span>
              <button
                onClick={() => fetchReports(filters)}
                className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-red-700"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {isLoading && (
          <div
            role="status"
            aria-live="polite"
            className="mb-6 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-700"
          >
            Atualizando relatórios...
          </div>
        )}

        {/* Cards de Resumo */}
        <div className={`${handles.summaryCards} mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4`}>
          <div className={`${handles.summaryCard} rounded-lg bg-white p-6 shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Receita Total</p>
                <p className={`${handles.summaryValue} text-2xl font-bold text-gray-900`}>
                  {formatPrice(summary?.totalRevenue ?? 0)}
                </p>
                <p
                  className={`${handles.reportChange} text-sm ${
                    (summary?.revenueGrowth ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatPercentage(summary?.revenueGrowth ?? 0)} vs período anterior
                </p>
              </div>
              <div className={`${handles.summaryIcon} flex h-12 w-12 items-center justify-center rounded-lg bg-green-100`}>
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>

          <div className={`${handles.summaryCard} rounded-lg bg-white p-6 shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Pedidos</p>
                <p className={`${handles.summaryValue} text-2xl font-bold text-gray-900`}>
                  {summary?.totalOrders ?? 0}
                </p>
                <p
                  className={`${handles.reportChange} text-sm ${
                    (summary?.ordersGrowth ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatPercentage(summary?.ordersGrowth ?? 0)} vs período anterior
                </p>
              </div>
              <div className={`${handles.summaryIcon} flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100`}>
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
            </div>
          </div>

          <div className={`${handles.summaryCard} rounded-lg bg-white p-6 shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Ticket Médio</p>
                <p className={`${handles.summaryValue} text-2xl font-bold text-gray-900`}>
                  {formatPrice(summary?.averageOrderValue ?? 0)}
                </p>
                <p
                  className={`${handles.reportChange} text-sm ${
                    (summary?.averageOrderValueGrowth ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatPercentage(summary?.averageOrderValueGrowth ?? 0)} vs período anterior
                </p>
              </div>
              <div className={`${handles.summaryIcon} flex h-12 w-12 items-center justify-center rounded-lg bg-yellow-100`}>
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className={`${handles.summaryCard} rounded-lg bg-white p-6 shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Taxa de Conversão</p>
                <p className={`${handles.summaryValue} text-2xl font-bold text-gray-900`}>
                  {(summary?.conversionRate ?? 0).toFixed(1)}%
                </p>
                <p
                  className={`${handles.reportChange} text-sm ${
                    (summary?.conversionRateGrowth ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatPercentage(summary?.conversionRateGrowth ?? 0)} vs período anterior
                </p>
              </div>
              <div className={`${handles.summaryIcon} flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100`}>
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Gráfico de Vendas Mensais */}
          <div className={`${handles.chartSection}`}>
            <div className={`${handles.chartCard} rounded-lg bg-white p-6 shadow-sm`}>
              <h3 className={`${handles.chartTitle} mb-4 text-lg font-semibold text-gray-900`}>
                Vendas Mensais
              </h3>
              <div className={`${handles.chartContent}`}>
                <AdminSalesChart data={salesTrend} isLoading={isLoading} />
              </div>
            </div>
          </div>

          {/* Top Produtos */}
          <div className={`${handles.tableSection}`}>
            <div className={`${handles.tableCard} rounded-lg bg-white p-6 shadow-sm`}>
              <h3 className={`${handles.tableTitle} mb-4 text-lg font-semibold text-gray-900`}>
                Top 5 Produtos
              </h3>
              <div className={`${handles.tableContent}`}>
                <div className="space-y-3">
                  {isLoading && topProducts.length === 0 ? (
                    <div className="flex min-h-[100px] items-center justify-center text-sm text-gray-500">
                      Carregando produtos...
                    </div>
                  ) : topProducts.length === 0 ? (
                    <div className="flex min-h-[100px] items-center justify-center text-sm text-gray-500">
                      Nenhum produto encontrado para o período.
                    </div>
                  ) : (
                    topProducts.map((product) => (
                      <div
                        key={product.id}
                        className={`${handles.tableRow} flex items-center justify-between gap-3 rounded-lg bg-gray-50 p-3`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                            <ImageWithFallback
                              src={buildImageUrl(product.imageUrl || undefined)}
                              alt={product.name}
                              className="h-full w-full object-cover"
                              fallbackWrapperClassName="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100"
                              fallbackIconClassName="h-6 w-6 text-gray-300"
                            />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{product.name}</p>
                            <p className="text-sm text-gray-600">
                              {product.sales} venda{product.sales === 1 ? '' : 's'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">{formatPrice(product.revenue)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Clientes */}
        <div className={`${handles.tableSection} mb-8`}>
          <div className={`${handles.tableCard} rounded-lg bg-white p-6 shadow-sm`}>
            <h3 className={`${handles.tableTitle} mb-4 text-lg font-semibold text-gray-900`}>
              Top 5 Clientes
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${handles.tableHeader} bg-gray-50`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Pedidos
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Total Gasto
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {isLoading && topCustomers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">
                        Carregando clientes...
                      </td>
                    </tr>
                  ) : topCustomers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">
                        Nenhum cliente encontrado para o período.
                      </td>
                    </tr>
                  ) : (
                    topCustomers.map((customer) => (
                      <tr key={customer.id} className={`${handles.tableRow} hover:bg-gray-50`}>
                        <td className={`${handles.tableCell} whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900`}>
                          {customer.name}
                        </td>
                        <td className={`${handles.tableCell} whitespace-nowrap px-4 py-4 text-sm text-gray-600`}>
                          {customer.email}
                        </td>
                        <td className={`${handles.tableCell} whitespace-nowrap px-4 py-4 text-sm text-gray-900`}>
                          {customer.orders}
                        </td>
                        <td className={`${handles.tableCell} whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900`}>
                          {formatPrice(customer.total)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <AdminFilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        isApplyDisabled={isApplyButtonDisabled}
        applyLabel="Aplicar filtros"
        clearLabel="Limpar filtros"
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor="admin-reports-filter-range"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Período
            </label>
            <Select
              id="admin-reports-filter-range"
              value={draftFilters.range}
              onChange={(value) =>
                handleDraftFilterChange({
                  range: value as AdminReportRange,
                  startDate: value === 'custom' ? draftFilters.startDate : '',
                  endDate: value === 'custom' ? draftFilters.endDate : ''
                })
              }
              options={Object.entries(RANGE_LABELS).map(([value, label]) => ({
                value,
                label
              }))}
              placeholder="Selecione o período"
              className="w-full"
            />
          </div>

          {draftFilters.range === 'custom' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="admin-reports-filter-start"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Data inicial
                </label>
                <Input
                  id="admin-reports-filter-start"
                  type="date"
                  value={draftFilters.startDate || ''}
                  onChange={(event) => handleDraftFilterChange({ startDate: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm p-inputtext"
                />
              </div>
              <div>
                <label
                  htmlFor="admin-reports-filter-end"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Data final
                </label>
                <Input
                  id="admin-reports-filter-end"
                  type="date"
                  value={draftFilters.endDate || ''}
                  onChange={(event) => handleDraftFilterChange({ endDate: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm p-inputtext"
                />
              </div>
              {isCustomRangeInvalid && (
                <p className="col-span-1 text-xs text-red-600 sm:col-span-2">
                  Informe as duas datas para aplicar o período personalizado.
                </p>
              )}
            </div>
          )}

          <div>
            <label
              htmlFor="admin-reports-filter-type"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Tipo de relatório
            </label>
            <Select
              id="admin-reports-filter-type"
              value={draftFilters.type}
              onChange={(value) =>
                handleDraftFilterChange({ type: value as AdminReportType })
              }
              options={Object.entries(TYPE_LABELS).map(([value, label]) => ({
                value,
                label
              }))}
              placeholder="Selecione o tipo"
              className="w-full"
            />
          </div>
        </div>
      </AdminFilterSidebar>
    </AdminLayout>
  )
}

export default AdminReports
